/**
 * Custom module home — /m/$moduleKey
 *
 * Shows the module's entities as clickable cards so the user can jump
 * to any entity's list view. This is the landing page when you click a
 * custom module in the sidebar.
 */

import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight, Box } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/m/$moduleKey/')({
  component: CustomModuleHome,
})

function CustomModuleHome() {
  const { moduleKey } = Route.useParams()

  const { data: module, isLoading: moduleLoading } = useQuery(
    orpc.modules.custom.modules.get.queryOptions({ input: { key: moduleKey } }),
  )
  const { data: entities, isLoading: entitiesLoading } = useQuery(
    orpc.modules.custom.entities.list.queryOptions({ input: { moduleKey } }),
  )

  if (moduleLoading || entitiesLoading) {
    return (
      <div className="container mx-auto py-6 px-4 space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      </div>
    )
  }

  if (!module) {
    return (
      <div className="container mx-auto py-6 px-4">
        <h1 className="text-2xl font-bold">Module not found</h1>
        <p className="text-muted-foreground mt-2">
          The module '{moduleKey}' doesn't exist or you don't have access to it.
        </p>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6 px-4 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{module.name}</h1>
        {module.description && (
          <p className="text-muted-foreground mt-1">{module.description}</p>
        )}
      </div>

      {entities && entities.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Box className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">
              This module doesn't have any entities yet.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Add entities from the module builder to start storing data.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {entities?.map((entity) => (
            <Link
              key={entity.id}
              to="/m/$moduleKey/$entityKey"
              params={{ moduleKey, entityKey: entity.key }}
            >
              <Card className="hover:bg-muted/50 transition-colors cursor-pointer h-full">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{entity.namePlural ?? entity.name}</span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {entity.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {entity.description}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    {(entity.fields as unknown[]).length} fields
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
